/**
 * PlayerControls — the pieces the mini player and «Сейчас играет» share: the words on the
 * buttons, their icons, the slider and the line that says what went wrong.
 *
 * Task 07 drew them inside the mini player; task 09 moved them here when «Сейчас играет»
 * needed the same slider and the same sentence. Two copies would drift, and a listener who
 * sees «Звук не идёт» on one screen and something else on the other stops trusting both.
 *
 * Like the mini player, everything here reads `store/player.ts` and calls `engine/player.ts`;
 * nothing touches the element.
 */

import type { CSSProperties, JSX } from 'react';

import { player } from '../engine/player';
import { formatDuration } from '../lib/format';
import type { PlayerProblem } from '../store/player';
import { isSounding, usePlayer } from '../store/player';
import { nextIndex, useQueue } from '../store/queue';

export const PLAY_LABEL = 'Играть';
export const PAUSE_LABEL = 'Пауза';
export const NEXT_LABEL = 'Следующий трек';
export const PREVIOUS_LABEL = 'Предыдущий трек';
export const QUEUE_BUTTON_LABEL = 'Очередь';
export const SEEK_LABEL = 'Перемотка';
export const RETRY_LABEL = 'Повторить';
export const LOADING_TEXT = 'Загружается…';

/**
 * Task 49: the loading line of a long track started where the listener stopped —
 * «Загружается… с 42:10», so a mix that does not start at 0:00 says why.
 */
export function loadingText(resumeFromS: number | undefined): string {
  return resumeFromS === undefined ? LOADING_TEXT : `${LOADING_TEXT} с ${clock(resumeFromS)}`;
}

/** What the player says for each problem, and whether it offers a button. */
export const PROBLEM_TEXT: Readonly<Record<PlayerProblem, string>> = Object.freeze({
  gesture: 'Телефон ждёт нажатия, чтобы включить звук.',
  stalled: 'Звук не идёт. Проверьте интернет и попробуйте ещё раз.',
  failed: 'Трек не загрузился. Попробуйте ещё раз.',
  gone: 'Этого трека больше нет в канале.',
});

export interface PlaybackFlags {
  /** Sound is asked for: the button shows «Пауза». */
  readonly playing: boolean;
  /** Sound is asked for and not coming yet, with nothing wrong: «Загружается…». */
  readonly loading: boolean;
}

/** The two things both players draw from the slice, worked out once. */
export function usePlaybackFlags(): PlaybackFlags {
  const status = usePlayer((s) => s.status);
  const buffering = usePlayer((s) => s.buffering);
  const problem = usePlayer((s) => s.problem);
  return {
    playing: isSounding(status),
    loading: problem === undefined && (status === 'loading' || buffering),
  };
}

/**
 * The queue has a track after this one for «Следующий». A boolean out of the queue: an edit
 * that does not change the answer re-renders nothing.
 */
export function useHasNext(): boolean {
  return useQueue((s) => nextIndex(s, 'manual') !== -1);
}

/** `0:00` for a known zero, and the same format as the list for everything else. */
function clock(seconds: number): string {
  return formatDuration(Math.floor(seconds)) || '0:00';
}

export interface IconProps {
  readonly className: string;
}

export function PlayIcon({ className }: IconProps): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className={className}>
      <path d="M8 5v14l11-7z" fill="currentColor" />
    </svg>
  );
}

export function PauseIcon({ className }: IconProps): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className={className}>
      <path d="M7 5h4v14H7zM13 5h4v14h-4z" fill="currentColor" />
    </svg>
  );
}

export function NextIcon({ className }: IconProps): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className={className}>
      <path d="M6 6l9 6-9 6zM16 6h2v12h-2z" fill="currentColor" />
    </svg>
  );
}

export function PreviousIcon({ className }: IconProps): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className={className}>
      <path d="M18 6l-9 6 9 6zM6 6h2v12H6z" fill="currentColor" />
    </svg>
  );
}

export function QueueIcon({ className }: IconProps): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className={className}>
      <path d="M4 6h12M4 11h12M4 16h8M17 14v6l4-3z" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

/**
 * The slider with the time on both sides. Its travel is the whole track, and a drag past what
 * the phone could load stops where the loaded part ends (`engine/readings.ts`, «How far the
 * slider may go»); the loaded part is drawn so the stop is not a surprise.
 */
export function SeekBar(): JSX.Element {
  const positionS = usePlayer((s) => s.positionS);
  const durationS = usePlayer((s) => s.durationS);
  const seekLimitS = usePlayer((s) => s.seekLimitS);

  const loadedShare = durationS > 0 ? Math.min(1, seekLimitS / durationS) : 0;
  const sliderStyle = { '--loaded': `${String(Math.round(loadedShare * 100))}%` } as CSSProperties;
  const max = Math.max(1, Math.round(durationS));

  return (
    <div className="seek">
      <span className="seekTime">{clock(positionS)}</span>
      <input
        type="range"
        className="seekSlider"
        aria-label={SEEK_LABEL}
        min={0}
        max={max}
        step={1}
        value={Math.min(Math.round(positionS), max)}
        style={sliderStyle}
        disabled={durationS <= 0}
        onChange={(event) => {
          player().seek(Number(event.currentTarget.value));
        }}
      />
      <span className="seekTime">{durationS > 0 ? clock(durationS) : ''}</span>
    </div>
  );
}

/**
 * Task 67: the mini player's time — a thin gold line along its top edge, no figures.
 *
 * The same slider as {@link SeekBar}, drawn as a line: gold as far as the track has played,
 * lighter as far as the phone could load. The figures are gone from the bar; a screen reader
 * still hears them, as the slider's value («1:13 из 3:07»). On an iPhone a slider moves only when
 * the finger takes its thumb, so the thumb is an invisible square of `--hit` at the line's gold
 * end, and the whole slider is `--hit` tall, half of it over the list above the bar.
 */
export function MiniSeek(): JSX.Element {
  const positionS = usePlayer((s) => s.positionS);
  const durationS = usePlayer((s) => s.durationS);
  const seekLimitS = usePlayer((s) => s.seekLimitS);

  const max = Math.max(1, Math.round(durationS));
  const value = Math.min(Math.round(positionS), max);
  const share = (part: number): string =>
    `${String(durationS > 0 ? Math.round(Math.min(1, part / durationS) * 1000) / 10 : 0)}%`;
  const style = { '--played': share(positionS), '--loaded': share(seekLimitS) } as CSSProperties;

  return (
    <input
      type="range"
      className="miniSeek"
      aria-label={SEEK_LABEL}
      aria-valuetext={
        durationS > 0 ? `${clock(positionS)} из ${clock(durationS)}` : clock(positionS)
      }
      min={0}
      max={max}
      step={1}
      value={value}
      style={style}
      disabled={durationS <= 0}
      onChange={(event) => {
        player().seek(Number(event.currentTarget.value));
      }}
    />
  );
}

/**
 * One sentence and one button, never a code: `spec.md` «Воспроизведение», story 32, asks for
 * «понятную ошибку с кнопкой „повторить“». Nothing at all while nothing is wrong.
 */
export function ProblemLine(): JSX.Element | null {
  const problem = usePlayer((s) => s.problem);
  if (problem === undefined) return null;
  return (
    <div className="problem" role="alert">
      <span>{PROBLEM_TEXT[problem]}</span>
      {problem !== 'gone' && (
        <button
          type="button"
          className="linkButton"
          onClick={() => {
            player().retry();
          }}
        >
          {problem === 'gesture' ? PLAY_LABEL : RETRY_LABEL}
        </button>
      )}
    </div>
  );
}
